/**
 * Frontend types for the audit viewer (T7 — plan §7.3).
 *
 * Mirrors :class:`ai_alm.models.events.PipelineEvent` and the
 * ``LLMUsageEvent`` payload added in T4. Kept hand-rolled rather than
 * codegenned so a backend literal-typed enum addition surfaces here
 * as a TS error (not a silent ``any`` slip-through).
 */

export type AuditEventType =
  | 'phase_started'
  | 'phase_progress'
  | 'phase_completed'
  | 'agent_started'
  | 'agent_progress'
  | 'agent_completed'
  | 'observer.partial_failure'
  | 'rewriter.proposed'
  | 'dedup_completed'
  | 'citation_report'
  | 'compliance_authoring_completed'
  | 'recipe_adjusted'
  | 'briefing_ready'
  | 'gate_ready'
  | 'artifact_written'
  | 'rqs_updated'
  | 'rewind_initiated'
  | 'export_ready'
  | 'budget_warning'
  | 'budget_halt'
  | 'subscribed'
  | 'pong'
  | 'error'
  | 'llm_usage'

export interface AuditEvent {
  /** Discriminator — the ``event`` Literal on the backend. */
  event: AuditEventType
  /** Project the event was emitted under (always set on persisted rows). */
  project_id: string | null
  /** Run id the event belongs to (1 for v0.6 single-run projects). */
  run_id: number | null
  /** Phase 1..5 when applicable; null for project-scoped events. */
  phase: number | null
  /** Agent key (``critic`` / ``observer`` / …) when applicable. */
  agent: string | null
  /** Free-form payload — shape depends on ``event``. */
  payload: Record<string, unknown>
  /** ISO-8601 timestamp the producer stamped on the row. */
  ts: string
}

export interface LLMUsagePayload {
  model: string
  prompt_sha: string
  input_tokens: number
  output_tokens: number
  cache_read_input_tokens: number
  cache_creation_input_tokens: number
  stop_reason: string | null
  latency_ms: number
  /** Path under ``audit/`` to the persisted call file. POSIX-normalised. */
  call_path: string | null
}

/**
 * Payload for ``rewriter.proposed`` events streamed from
 * :func:`ai_alm.services.phase4_runner.run_rewrites`. Carries the
 * before/after pair the audit viewer renders as an inline diff
 * (T7 — plan §7.4).
 *
 * ``status`` reflects what the rewriter agent decided about the
 * proposal: ``proposed`` (default), ``accepted`` (the architect
 * committed via ``/rewrites/apply``), or ``skipped`` /
 * ``superseded`` when the rewriter dropped or replaced its own
 * suggestion. The viewer surfaces the chip so a stale ``proposed``
 * row doesn't read like an open action item.
 */
export interface RewriterProposedPayload {
  finding_id: string
  rewrite_id: string
  requirement_id: string
  rule: string
  severity: string
  before: string
  after: string
  status: string
  status_reason?: string | null
  /** POSIX-normalised path under the project root pointing at the
   *  on-disk rewrite file. Useful for "open in editor" affordances
   *  the viewer may add later. */
  path: string
}

export interface AuditVerifyResponse {
  ok: boolean
  length: number
  diag: string
}

/**
 * On-disk shape of one ``audit/llm/<seq>_<agent>.json`` file
 * persisted by :func:`ai_alm.services.llm_audit.persist_llm_call`.
 *
 * The file is the *redacted* round-trip — secret-shaped fields
 * (api keys, auth headers) are scrubbed before write. The frontend
 * surfaces it in the audit viewer's side panel when the user
 * clicks an ``llm_usage`` row.
 */
export interface LLMCallFile {
  agent: string
  request: {
    model: string
    system: string
    messages: Array<{ role: string; content: unknown }>
    extra?: Record<string, unknown>
    [key: string]: unknown
  }
  response: {
    text: string
    model: string
    input_tokens: number
    output_tokens: number
    cache_read_input_tokens: number
    cache_creation_input_tokens: number
    stop_reason: string | null
    duration_ms: number
  }
}

/**
 * Build the URL for one persisted LLM call file. The
 * :class:`AuditWriter` writes under
 * ``runs/run-{NNN}/audit/llm/<seq>_<agent>.json``; the
 * ``call_path`` carried by an ``llm_usage`` event is *relative to
 * the audit directory* (POSIX-normalised). We rejoin the run
 * prefix here so the existing read-only file route
 * (:py:mod:`ai_alm.api.routes.files`) serves it directly.
 */
export function llmCallFileUrl(
  projectId: string,
  runId: number,
  callPath: string,
): string {
  const run = String(runId).padStart(3, '0')
  // ``encodeURIComponent`` would mangle the ``/`` in ``llm/0001_critic.json``;
  // the file route's ``{path:path}`` parameter accepts unescaped slashes.
  return `/api/projects/${encodeURIComponent(projectId)}/files/runs/run-${run}/audit/${callPath}`
}

/**
 * One phase's worth of timeline entries — used by the audit viewer to
 * render duration bars and section headers without re-walking the
 * timeline on every render.
 */
export interface AuditPhaseGroup {
  phase: number | null
  /** Events in arrival order. Always at least one entry. */
  events: AuditEvent[]
  /** ISO timestamp of the first event in the group. */
  startTs: string
  /** ISO timestamp of the last event in the group. */
  endTs: string
  /** ``phase_completed - phase_started`` in milliseconds when both events
   *  exist; null when the phase is still running. */
  durationMs: number | null
}

/**
 * Group an arrival-ordered timeline into per-phase buckets.
 *
 * Events without a ``phase`` collapse into the special ``null`` group
 * (typically ``subscribed`` / ``llm_usage`` / ``budget_*`` etc).
 */
export function groupByPhase(events: AuditEvent[]): AuditPhaseGroup[] {
  const buckets = new Map<number | null, AuditEvent[]>()
  for (const event of events) {
    const key = event.phase ?? null
    const bucket = buckets.get(key)
    if (bucket) {
      bucket.push(event)
    } else {
      buckets.set(key, [event])
    }
  }
  const groups: AuditPhaseGroup[] = []
  // Phase-numbered buckets first (1, 2, 3, …); the ``null`` bucket
  // sits at the end so cross-phase events render as a footer.
  const phaseKeys = Array.from(buckets.keys())
    .filter((k): k is number => typeof k === 'number')
    .sort((a, b) => a - b)
  for (const phase of phaseKeys) {
    const bucket = buckets.get(phase)!
    groups.push(buildGroup(phase, bucket))
  }
  if (buckets.has(null)) {
    groups.push(buildGroup(null, buckets.get(null)!))
  }
  return groups
}

function buildGroup(phase: number | null, events: AuditEvent[]): AuditPhaseGroup {
  const startTs = events[0].ts
  const endTs = events[events.length - 1].ts
  let durationMs: number | null = null
  const started = events.find((e) => e.event === 'phase_started')
  const completed = events.find((e) => e.event === 'phase_completed')
  if (started && completed) {
    const dt = Date.parse(completed.ts) - Date.parse(started.ts)
    if (!Number.isNaN(dt)) durationMs = dt
  }
  return { phase, events, startTs, endTs, durationMs }
}
